(() => {
  const productRoot = () => document.querySelector('.tember-product-shell')

  class TemberLocalizedGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return
      this.initialized = true
      this.currentIndex = 0
      this.swipeStarts = { pointer: null, touch: null }
      this.lastSwipeAt = 0
      this.addEventListener('click', (event) => {
        const target = event.target
        if (!(target instanceof Element)) return
        const thumbnail = target.closest('[data-gallery-thumbnail]')
        if (thumbnail instanceof HTMLButtonElement) {
          this.currentIndex = Number(thumbnail.dataset.galleryIndex || 0)
          this.render()
          return
        }
        if (target.closest('[data-gallery-previous]')) {
          this.move(-1)
          return
        }
        if (target.closest('[data-gallery-next]')) this.move(1)
      })
      const stage = this.querySelector('.tember-localized-gallery__stage')
      const startSwipe = (kind, pointerId, x, y) => {
        this.swipeStarts[kind] = { pointerId, x, y, horizontal: false }
      }
      const trackSwipe = (kind, pointerId, x, y, event) => {
        const start = this.swipeStarts[kind]
        if (!start || start.pointerId !== pointerId) return
        const horizontalDistance = x - start.x
        const verticalDistance = y - start.y
        if (
          Math.abs(horizontalDistance) >= 10
          && Math.abs(horizontalDistance) > Math.abs(verticalDistance) * 1.05
        ) {
          start.horizontal = true
          if (event?.cancelable) event.preventDefault()
        }
      }
      const finishSwipe = (kind, pointerId, x, y) => {
        const start = this.swipeStarts[kind]
        if (!start || start.pointerId !== pointerId) return
        const horizontalDistance = x - start.x
        const verticalDistance = y - start.y
        this.swipeStarts[kind] = null
        if (
          Math.abs(horizontalDistance) >= 24
          && Math.abs(horizontalDistance) > Math.abs(verticalDistance) * 1.05
          && Date.now() - this.lastSwipeAt > 400
        ) {
          this.lastSwipeAt = Date.now()
          this.move(horizontalDistance < 0 ? 1 : -1)
        }
      }
      stage?.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) return
        if (event.target instanceof Element && event.target.closest('[data-gallery-previous], [data-gallery-next]')) return
        startSwipe('pointer', event.pointerId, event.clientX, event.clientY)
      })
      stage?.addEventListener('pointermove', (event) => {
        trackSwipe('pointer', event.pointerId, event.clientX, event.clientY, event)
      })
      stage?.addEventListener('pointerup', (event) => {
        finishSwipe('pointer', event.pointerId, event.clientX, event.clientY)
      })
      stage?.addEventListener('pointercancel', () => {
        this.swipeStarts.pointer = null
      })
      // iOS versions that cancel Pointer Events during a horizontal image
      // gesture still deliver Touch Events. This fallback shares the same
      // direction test and deduplication clock, so one swipe advances once.
      stage?.addEventListener('touchstart', (event) => {
        const touch = event.touches[0]
        if (!touch) return
        startSwipe('touch', touch.identifier, touch.clientX, touch.clientY)
      }, { passive: true })
      stage?.addEventListener('touchmove', (event) => {
        if (event.touches.length !== 1) return
        const touch = event.touches[0]
        if (!touch) return
        trackSwipe('touch', touch.identifier, touch.clientX, touch.clientY, event)
      }, { passive: false })
      stage?.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0]
        if (!touch) return
        finishSwipe('touch', touch.identifier, touch.clientX, touch.clientY)
      }, { passive: true })
      stage?.addEventListener('touchcancel', () => {
        this.swipeStarts.touch = null
      }, { passive: true })
      this.render()
    }

    setDesign(design) {
      const nextGroup = design
      if (this.dataset.galleryDesign !== nextGroup) this.currentIndex = 0
      this.dataset.galleryDesign = nextGroup
      this.render()
    }

    activeSlides() {
      return [...this.querySelectorAll('[data-gallery-slide]')]
        .filter((slide) => slide.dataset.galleryGroup === this.dataset.galleryDesign)
    }

    move(step) {
      const slides = this.activeSlides()
      if (!slides.length) return
      this.currentIndex = (this.currentIndex + step + slides.length) % slides.length
      this.render()
    }

    render() {
      const group = this.dataset.galleryDesign
      const slides = this.activeSlides()
      if (!slides.length) return
      if (this.currentIndex >= slides.length) this.currentIndex = 0

      this.querySelectorAll('[data-gallery-slide]').forEach((slide) => {
        const active = slide.dataset.galleryGroup === group
          && Number(slide.dataset.galleryIndex) === this.currentIndex
        slide.hidden = !active
        slide.setAttribute('aria-hidden', String(!active))
      })

      this.querySelectorAll('[data-gallery-thumbnail]').forEach((thumbnail) => {
        const inGroup = thumbnail.dataset.galleryGroup === group
        const active = inGroup && Number(thumbnail.dataset.galleryIndex) === this.currentIndex
        thumbnail.hidden = !inGroup
        if (active) {
          thumbnail.setAttribute('aria-current', 'true')
          thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
        } else {
          thumbnail.removeAttribute('aria-current')
        }
      })

      const current = this.querySelector('[data-gallery-current]')
      const total = this.querySelector('[data-gallery-total]')
      if (current) current.textContent = String(this.currentIndex + 1)
      if (total) total.textContent = String(slides.length)
    }
  }

  if (!customElements.get('tember-localized-gallery')) {
    customElements.define('tember-localized-gallery', TemberLocalizedGallery)
  }

  const designSlug = (value = '') => {
    const slug = value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

    if (['galaxy', 'galaxia', 'galaxie', 'galassia'].some((name) => slug.includes(name))) return 'galaxy'
    if (['avocado', 'aguacate', 'avocat', 'avokado'].some((name) => slug.includes(name))) return 'avocado'
    if (['banana', 'platano', 'banane'].some((name) => slug.includes(name))) return 'banana'
    if (['watermelon', 'sandia', 'pasteque', 'wassermelone', 'anguria'].some((name) => slug.includes(name))) return 'watermelon'
    if (['cherry', 'cereza', 'cerise', 'kirsche', 'ciliegia'].some((name) => slug.includes(name))) return 'cherry'
    return 'four-colour'
  }

  const refreshLocalizedGallery = (value) => {
    let selectedValue = value
    if (!selectedValue) {
      selectedValue = productRoot()
        ?.querySelector('[data-tember-variant-picker] select')
        ?.value
    }
    productRoot()
      ?.querySelectorAll('tember-localized-gallery')
      .forEach((gallery) => gallery.setDesign(designSlug(selectedValue)))
  }

  const selectRequestedDesign = () => {
    const requested = new URLSearchParams(window.location.search)
      .get('design')
      ?.trim()
      .toLowerCase()
    const supported = [
      'galaxy',
      'four-colour',
      'avocado',
      'banana',
      'watermelon',
      'cherry',
    ]
    if (!requested || !supported.includes(requested)) return

    const input = productRoot()?.querySelector(
      `[data-tember-design="${requested}"]`,
    )
    if (!(input instanceof HTMLElement) || input.getAttribute('aria-pressed') === 'true') return
    input.click()
  }

  const refreshDesignButtons = (root = productRoot()) => {
    root?.querySelectorAll('.tember-design-picker').forEach((wrapper) => {
      const select = wrapper.querySelector('.tember-design-select')
      if (!(select instanceof HTMLSelectElement)) return

      wrapper.querySelectorAll('[data-tember-design-button]').forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return
        const option = [...select.options].find((item) => item.value === button.dataset.temberOptionValue)
        button.setAttribute('aria-pressed', String(select.value === button.dataset.temberOptionValue))
        if (option) button.disabled = option.disabled
      })
    })
  }

  // iOS Safari can still show disabled or hidden <option> rows in its native
  // picker. Keep the complete option list in memory and physically detach
  // pack sizes that do not belong to the selected design. Changing design
  // restores the matching set, so 4-based and 6-based products never leak
  // into one another.
  const pickerVariantData = new WeakMap()
  const selectOptionPools = new WeakMap()

  const getPickerVariantData = (picker) => {
    if (pickerVariantData.has(picker)) return pickerVariantData.get(picker)
    const source = picker.querySelector('script[type="application/json"]')
    let variants = []
    try {
      variants = JSON.parse(source?.textContent || '[]')
    } catch (_error) {
      variants = []
    }
    pickerVariantData.set(picker, variants)
    return variants
  }

  const getSelectOptionPool = (select) => {
    if (!selectOptionPools.has(select)) {
      selectOptionPools.set(
        select,
        [...select.options].filter((option) => option.hasAttribute('data-tember-option-value')),
      )
    }
    return selectOptionPools.get(select)
  }

  const syncPickerAvailability = (picker) => {
    if (!picker?.matches('variant-selects')) return
    const variants = getPickerVariantData(picker).filter((variant) => variant.available)
    const selects = [...picker.querySelectorAll('select')]
    const chosen = []

    selects.forEach((select, index) => {
      const pool = getSelectOptionPool(select)
      const matching = variants.filter((variant) =>
        chosen.every((value, previousIndex) => variant.options[previousIndex] === value),
      )
      const availableValues = new Set(matching.map((variant) => variant.options[index]))

      pool.forEach((option) => {
        const available = availableValues.has(option.value)
        option.hidden = !available
        option.disabled = !available
        if (available) select.append(option)
        else option.remove()
      })

      let selected = pool.find(
        (option) => option.value === select.value && availableValues.has(option.value),
      )
      if (!selected) selected = pool.find((option) => availableValues.has(option.value))
      if (selected) select.value = selected.value
      chosen.push(select.value)
    })
  }

  const selectedPickerValues = (picker) => {
    if (picker.matches('variant-selects')) {
      return [...picker.querySelectorAll('select')].map((select) => select.value)
    }

    return [...picker.querySelectorAll('fieldset')].map(
      (fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value,
    )
  }

  const syncVariantPickers = (sourcePicker) => {
    syncPickerAvailability(sourcePicker)
    const values = selectedPickerValues(sourcePicker)
    if (!values.length || values.some((value) => !value)) return

    productRoot()
      ?.querySelectorAll('[data-tember-variant-picker]')
      .forEach((targetPicker) => {
        if (targetPicker === sourcePicker) return

        if (targetPicker.matches('variant-selects')) {
          const selects = [...targetPicker.querySelectorAll('select')]
          if (selects[0] && values[0] !== undefined) selects[0].value = values[0]
          syncPickerAvailability(targetPicker)
          selects.slice(1).forEach((select, offset) => {
            const value = values[offset + 1]
            if (value !== undefined && [...select.options].some((option) => option.value === value)) {
              select.value = value
            }
          })
          syncPickerAvailability(targetPicker)
        } else {
          targetPicker.querySelectorAll('fieldset').forEach((fieldset, index) => {
            const radio = [...fieldset.querySelectorAll('input[type="radio"]')]
              .find((input) => input.value === values[index])
            if (radio) radio.checked = true
          })
        }

        targetPicker.syncAvailableOptions?.()
        targetPicker.updateOptions?.()
        targetPicker.updateMasterId?.()
      })
    refreshDesignButtons()
  }

  const playLoadedProductVideo = (poster) => {
    const media = poster.closest('deferred-media')
    if (!media) return

    window.setTimeout(() => {
      const video = media.querySelector('video')
      if (!(video instanceof HTMLVideoElement)) return
      video.muted = false
      video.play().catch(() => {
        video.controls = true
      })
    }, 0)
  }

  document.addEventListener('DOMContentLoaded', () => {
    productRoot()
      ?.querySelectorAll('[data-tember-variant-picker]')
      .forEach((picker) => syncPickerAvailability(picker))
    selectRequestedDesign()
    refreshDesignButtons()
    window.queueMicrotask(() => refreshLocalizedGallery())
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const selectedDesign = event?.data?.variant?.options?.[0]
        if (selectedDesign) refreshLocalizedGallery(selectedDesign)
      })
    }
  }, { once: true })

  document.addEventListener('click', (event) => {
    const target = event.target
    if (!(target instanceof Element)) return
    const designButton = target.closest('[data-tember-design-button]')
    if (designButton instanceof HTMLButtonElement) {
      const picker = designButton.closest('variant-selects')
      const select = picker?.querySelector('.tember-design-select')
      if (select instanceof HTMLSelectElement && !designButton.disabled) {
        select.value = designButton.dataset.temberOptionValue || ''
        select.dispatchEvent(new Event('change', { bubbles: true }))
      }
      return
    }
    const poster = target.closest('.tember-product-shell .deferred-media__poster')
    if (poster) playLoadedProductVideo(poster)
  })

  document.addEventListener('change', (event) => {
    const target = event.target
    if (!(target instanceof Element)) return
    const picker = target.closest('[data-tember-variant-picker]')
    if (!picker || !productRoot()?.contains(picker)) return
    window.queueMicrotask(() => {
      syncPickerAvailability(picker)
      syncVariantPickers(picker)
      refreshDesignButtons()
      refreshLocalizedGallery(selectedPickerValues(picker)[0])
    })
  })
})()
